import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { AppLayout } from '../../components/AppLayout';
import { OpusDashboard } from '../../components/Projects/OpusDashboard';
import { CreateSubProjectModal } from '../../components/Projects/CreateSubProjectModal';
import { Button } from '../../components/ui';
import { useOpusDashboard } from '../../hooks';
import { CreateSubProjectRequest, ProjectDto } from '../../services';
import './OpusDashboardPage.css';

export const OpusDashboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    opus,
    pieces,
    isLoading,
    error,
    createPiece,
    completePiece,
    archivePiece,
    movePieceToStage,
  } = useOpusDashboard({ opusId: id || '' });

  const handleBack = useCallback(() => {
    navigate('/projects');
  }, [navigate]);

  const handleAddPiece = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreatePiece = useCallback(
    async (data: CreateSubProjectRequest) => {
      await createPiece(data);
      setShowCreateModal(false);
    },
    [createPiece]
  );

  const handlePieceClick = useCallback(
    (piece: ProjectDto) => {
      navigate(`/projects/${piece.id}`);
    },
    [navigate]
  );

  const handleEditPiece = useCallback((piece: ProjectDto) => {
    // Navigate to the piece's detail page for editing
    // In a future enhancement, could open an edit modal
    navigate(`/projects/${piece.id}`);
  }, [navigate]);

  const handleCompletePiece = useCallback(
    async (piece: ProjectDto) => {
      await completePiece(piece.id);
    },
    [completePiece]
  );

  const handleArchivePiece = useCallback(
    async (piece: ProjectDto) => {
      await archivePiece(piece.id);
    },
    [archivePiece]
  );

  const handleMovePiece = useCallback(
    async (pieceId: string, newStage: string) => {
      await movePieceToStage(pieceId, newStage);
    },
    [movePieceToStage]
  );

  // Error state
  if (error && !opus) {
    return (
      <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
        <div className="opus-dashboard-page opus-dashboard-page--error">
          <div className="opus-dashboard-page__error-content">
            <AlertCircle size={48} />
            <h2>{t('opusDashboard.error.title', 'Error Loading Opus')}</h2>
            <p>{error}</p>
            <Button variant="primary" onClick={handleBack}>
              {t('opusDashboard.error.backToProjects', 'Back to Projects')}
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Loading state with no opus yet
  if (isLoading && !opus) {
    return (
      <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
        <div className="opus-dashboard-page opus-dashboard-page--loading">
          <div className="loading-spinner" />
          <p>{t('opusDashboard.loading', 'Loading opus...')}</p>
        </div>
      </AppLayout>
    );
  }

  // Opus not found
  if (!opus) {
    return (
      <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
        <div className="opus-dashboard-page opus-dashboard-page--not-found">
          <div className="opus-dashboard-page__error-content">
            <AlertCircle size={48} />
            <h2>{t('opusDashboard.notFound.title', 'Opus Not Found')}</h2>
            <p>{t('opusDashboard.notFound.message', 'The requested opus could not be found.')}</p>
            <Button variant="primary" onClick={handleBack}>
              {t('opusDashboard.error.backToProjects', 'Back to Projects')}
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout showBackButton backPath="/projects" backLabel={t('works.backToWorks')}>
      <div className="opus-dashboard-page">
        <OpusDashboard
          opus={opus}
          pieces={pieces}
          isLoading={isLoading}
          onBack={handleBack}
          onAddPiece={handleAddPiece}
          onPieceClick={handlePieceClick}
          onEditPiece={handleEditPiece}
          onArchivePiece={handleArchivePiece}
          onCompletePiece={handleCompletePiece}
          onMovePiece={handleMovePiece}
        />

        {/* Create Piece Modal */}
        <CreateSubProjectModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePiece}
          parentProjectTitle={opus.title}
          defaultProjectType={opus.projectType}
        />
      </div>
    </AppLayout>
  );
};

export default OpusDashboardPage;
